/**
 * useFirstSendPreview: who the FIRST send of an SOP would reach (docs/plans/
 * sop-import-plan.md 4.4 item 6). The first send creates the SOP's training
 * course, so POST /admin/campaigns/preview (useCampaignPreview) has no course to
 * preview; the send route itself answers with preview: true, after its whole
 * pre-flight, writing nothing (api/sops.ts previewSopSend).
 *
 *   const { preview, loading, error, reload } =
 *     useFirstSendPreview({ sopId, versionId, enabled, mode, audience, force });
 *
 * The same contract as useCampaignPreview: asks again whenever the inputs
 * change, keeps the latest answer, drops an answer for inputs that have since
 * changed, asks nothing (preview null) while disabled or the audience names
 * nobody. The untick is NOT sent: the caller keeps the excluded ids and filters
 * the list itself (AudiencePreview).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorMessage } from '../../api/client';
import { audienceIsEmpty } from '../../api/campaigns';
import type { CampaignAudience } from '../../api/campaigns';
import { previewSopSend } from '../../api/sops';
import type { SopSendMode } from '../../api/sops';
import type { CampaignPreviewState } from '../../components/pickers/useCampaignPreview';

export interface FirstSendPreviewInput {
  sopId: string;
  versionId: string;
  /** Only a first send (no training course yet) asks this route. */
  enabled: boolean;
  mode: SopSendMode;
  audience: CampaignAudience;
  force: boolean;
}

export function useFirstSendPreview({ sopId, versionId, enabled, mode, audience, force }: FirstSendPreviewInput): CampaignPreviewState {
  const [preview, setPreview] = useState<CampaignPreviewState['preview']>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const request = useRef(0);

  // One stable key for the inputs, so a re-render with equal values asks nothing.
  const key = JSON.stringify({ sopId, versionId, enabled, mode, audience, force });

  useEffect(() => {
    const id = ++request.current;
    const input = JSON.parse(key) as FirstSendPreviewInput;
    if (!input.enabled || audienceIsEmpty(input.audience)) {
      setPreview(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    previewSopSend(input.sopId, input.versionId, { mode: input.mode, audience: input.audience, force: input.force })
      .then((answer) => {
        if (id !== request.current) return;
        setPreview({
          recipients: Array.isArray(answer?.recipients) ? answer.recipients : [],
          count: typeof answer?.count === 'number' ? answer.count : 0,
        });
      })
      .catch((err) => {
        if (id !== request.current) return;
        setPreview(null);
        setError(apiErrorMessage(err, 'Could not work out who this would reach.'));
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, [key, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return { preview, loading, error, reload };
}

export default useFirstSendPreview;
